import React from 'react';
import { Link } from 'react-router-dom';
import { Typography } from '@mui/material';
import './About.css';
import Seo from '../common/Seo';
import { Footer } from '../layout';

const About = () => {
  return (
    <div className="ntb-about-container">
      <Seo
        title="About Us - NanoTradingCopilot"
        description="We help traders avoid costly mistakes by automating discipline. Learn about our mission to improve forex trading through validation."
        keywords="about NanoTradingCopilot, trading discipline, forex mission, trading psychology"
      />

      {/* Hero */}
      <header className="ntb-about-header">
        <Typography variant="h1" className="ntb-about-title">
          About NanoTradingCopilot
        </Typography>
        <Typography variant="body1" className="ntb-about-subtitle">
          We help traders avoid costly mistakes by automating discipline.
          Validate every trade against your own rules before you execute.
        </Typography>
      </header>

      {/* Problem / Solution */}
      <section className="ntb-about-section">
        <div className="ntb-about-grid">
          <div className="ntb-about-card">
            <div className="ntb-card-icon ntb-card-icon--problem">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <Typography variant="h3" className="ntb-card-title">The Problem</Typography>
            <Typography variant="body2" className="ntb-card-text">
              Traders know what they should do. But emotions get in the way.
              They skip checklists, ignore stops, and take trades that don't
              meet their criteria. The result: preventable losses.
            </Typography>
          </div>
          <div className="ntb-about-card">
            <div className="ntb-card-icon ntb-card-icon--solution">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <Typography variant="h3" className="ntb-card-title">The Solution</Typography>
            <Typography variant="body2" className="ntb-card-text">
              NanoTradingCopilot enforces your discipline automatically.
              Define your rules once, then validate every trade before execution.
              Get a clear pass/fail gate result with reasoning.
            </Typography>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="ntb-about-section">
        <Typography variant="h2" className="ntb-section-title">
          What We Offer
        </Typography>
        <div className="ntb-about-features-grid">
          <div className="ntb-about-feature">
            <div className="ntb-about-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-about-feature-title">
              Configurable Rules
            </Typography>
            <Typography variant="body2" className="ntb-about-feature-desc">
              Customize risk limits, R:R thresholds, and timing constraints to match your strategy
            </Typography>
          </div>
          <div className="ntb-about-feature">
            <div className="ntb-about-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-about-feature-title">
              Pass/Fail Gate Results
            </Typography>
            <Typography variant="body2" className="ntb-about-feature-desc">
              Clear pass or fail results with detailed reasoning for every rule checked
            </Typography>
          </div>
          <div className="ntb-about-feature">
            <div className="ntb-about-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-about-feature-title">
              Real-Time Market Data
            </Typography>
            <Typography variant="body2" className="ntb-about-feature-desc">
              Live forex prices and technical indicators fed directly into your validation pipeline
            </Typography>
          </div>
          <div className="ntb-about-feature">
            <div className="ntb-about-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-about-feature-title">
              AI-Assisted Analysis
            </Typography>
            <Typography variant="body2" className="ntb-about-feature-desc">
              Optional AI rules provide contextual market assessment — advisory, never predictive
            </Typography>
          </div>
          <div className="ntb-about-feature">
            <div className="ntb-about-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-about-feature-title">
              Trading Journal
            </Typography>
            <Typography variant="body2" className="ntb-about-feature-desc">
              PnL calendar, equity curve, and performance stats — log trades manually or via webhooks
            </Typography>
          </div>
          <div className="ntb-about-feature">
            <div className="ntb-about-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-about-feature-title">
              Mobile-Friendly
            </Typography>
            <Typography variant="body2" className="ntb-about-feature-desc">
              Validate trades on any device with a responsive interface built for speed
            </Typography>
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="ntb-about-section">
        <Typography variant="h2" className="ntb-section-title">
          Technology
        </Typography>
        <div className="ntb-about-tech-grid">
          <div className="ntb-about-tech-item">
            <Typography variant="h5" className="ntb-about-tech-label">Static Rules</Typography>
            <Typography variant="body2" className="ntb-about-tech-desc">
              Deterministic validation — instant results based on proven risk management principles
            </Typography>
          </div>
          <div className="ntb-about-tech-item">
            <Typography variant="h5" className="ntb-about-tech-label">AI Rules</Typography>
            <Typography variant="body2" className="ntb-about-tech-desc">
              LLM-powered contextual analysis — advisory only, never predictive or signal-generating
            </Typography>
          </div>
          <div className="ntb-about-tech-item">
            <Typography variant="h5" className="ntb-about-tech-label">Real-Time Data</Typography>
            <Typography variant="body2" className="ntb-about-tech-desc">
              Live market feeds for accurate spread, volatility, and trend validation checks
            </Typography>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="ntb-about-section ntb-about-contact">
        <Typography variant="h2" className="ntb-section-title">
          Contact
        </Typography>
        <Typography variant="body1" className="ntb-about-text">
          Questions, feedback, or ideas? We'd love to hear from you.
        </Typography>
        <a href="mailto:support@nanotradingcopilot.com" className="ntb-contact-link">
          support@nanotradingcopilot.com
        </a>
      </section>

      {/* CTA */}
      <section className="ntb-about-cta">
        <Typography variant="h2" className="ntb-about-cta-title">
          Ready to Trade with Discipline?
        </Typography>
        <Typography variant="body1" className="ntb-about-cta-subtitle">
          Free to start. No credit card required.
        </Typography>
        <Link to="/signup" className="ntb-btn ntb-btn-primary">
          Get Early Access
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default About;
